import { Close } from "@components/icons"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { h3Styles, smallcapsRegular13Styles } from "@constants/typography"
import { getIn } from "@utils/index"
import { DesktopOnly } from "@utils/MediaQuery"
import { Link } from "gatsby"
import React, { useEffect } from "react"
import styled from "styled-components"

const Container = styled.div<{ $isOpen?: boolean }>`
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0 calc(1 / 12 * 100vw);
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-evenly;
  will-change: opacity, transform;
  transition: opacity 200ms ${cubicBezier};
  background: ${colors.offWhite};
  opacity: ${(props) => (props.$isOpen ? "1" : "0")};
  overflow-y: auto;
  z-index: 1001;
  cursor: default;
  pointer-events: ${(props) => (props.$isOpen ? "" : "none")};
`

const Content = styled.div<{ $isOpen?: boolean }>`
  display: flex;
  height: 100%;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-evenly;
  will-change: opacity;
  transition: opacity 300ms ${cubicBezier} 50ms;
  opacity: ${(props) => (props.$isOpen ? "1" : "0")};
`

const Menu = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
`

const linkStyles = `
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`

const InternalLink = styled(Link)`
  ${linkStyles}
`

const ExternalLink = styled.a`
  ${linkStyles}
`

const LogoutButton = styled.span`
  ${linkStyles}
`

const Text = styled.div`
  ${h3Styles}
  padding: 32px 0;
  will-change: transform;
  transform: translateX(64px);
  transition: transform ${animation.medium} ${cubicBezier};
  background: ${colors.offWhite};

  @media ${breakpoints.laptop} {
    ${InternalLink}:hover & {
      transform: translateX(0);
    }
    ${ExternalLink}:hover & {
      transform: translateX(0);
    }
    ${LogoutButton}:hover & {
      transform: translateX(0);
    }
  }
`

const Dash = styled.div`
  width: 30px;
  height: 1px;
  margin-left: 34px;
  will-change: transform;
  transform: translateX(64px);
  transition:
    opacity ${animation.medium} ${cubicBezier} 50ms,
    transform ${animation.medium} ${cubicBezier} 50ms;
  background: ${colors.black};
  opacity: 0;

  @media ${breakpoints.laptop} {
    ${InternalLink}:hover & {
      transform: translateX(0);
      opacity: 1;
    }
    ${ExternalLink}:hover & {
      transform: translateX(0);
      opacity: 1;
    }
    ${LogoutButton}:hover & {
      transform: translateX(0);
      opacity: 1;
    }
  }
`

const Button = styled.button`
  display: flex;
  width: 56px;
  height: 56px;
  padding: 8px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${colors.dividerLineGray};
  border: none;
  border-radius: 2px;
  cursor: pointer;

  :focus {
    outline: none;
  }
`

const Footer = styled.div`
  display: flex;
  flex-direction: row;
`

const footerLinkStyles = `
  ${smallcapsRegular13Styles}
  margin-left: 60px;
  will-change: opacity;
  transition: opacity ${animation.quick} ${cubicBezier};
  opacity: 0.2;

  @media ${breakpoints.laptop} {
    &:hover {
      opacity: 0.3;
    }
  }
`

const InternalFooterLink = styled(Link)`
  ${footerLinkStyles}
`

const ExternalFooterLink = styled.a`
  ${footerLinkStyles}
`

interface DesktopMenuProps {
  globalData: any
  isLoggedIn?: boolean
  isOpen?: boolean
  onLogout?: () => void
  onRequestClose?: () => void
}

const DesktopMenu = (props: DesktopMenuProps) => {
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27 && props.onRequestClose) {
        props.onRequestClose()
      }
    }
    window.addEventListener("keydown", handleEsc)

    return () => {
      window.removeEventListener("keydown", handleEsc)
    }
  }, [])

  return (
    <DesktopOnly>
      <Container $isOpen={props.isOpen}>
        <Content $isOpen={props.isOpen}>
          <Button aria-label="Close Menu" onClick={props.onRequestClose}>
            <Close />
          </Button>
          <Menu>
            <InternalLink onClick={props.onRequestClose} to="/">
              <Text>Home</Text>
              <Dash />
            </InternalLink>
            <InternalLink onClick={props.onRequestClose} to="/collections/all/">
              <Text>Shop</Text>
              <Dash />
            </InternalLink>
            <InternalLink onClick={props.onRequestClose} to="/explore/">
              <Text>Explore</Text>
              <Dash />
            </InternalLink>
            <ExternalLink
              href={getIn(props, "globalData.supportLink", "")}
              onClick={props.onRequestClose}
            >
              <Text>Support</Text>
              <Dash />
            </ExternalLink>
            <InternalLink onClick={props.onRequestClose} to="/rewards/">
              <Text>Rewards</Text>
              <Dash />
            </InternalLink>
            <InternalLink onClick={props.onRequestClose} to="/account/">
              <Text>Account</Text>
              <Dash />
            </InternalLink>
            {props.isLoggedIn && (
              <LogoutButton onClick={props.onLogout}>
                <Text>Logout</Text>
                <Dash />
              </LogoutButton>
            )}
          </Menu>
          <Footer>
            <ExternalFooterLink
              href={getIn(props, "globalData.enterpriseLink", "")}
              onClick={props.onRequestClose}
            >
              Enterprise
            </ExternalFooterLink>
            <InternalFooterLink
              onClick={props.onRequestClose}
              to="/business-accounts"
            >
              Business Inquiries
            </InternalFooterLink>
            <InternalFooterLink onClick={props.onRequestClose} to="/dealscout">
              Dealer Programs
            </InternalFooterLink>
          </Footer>
        </Content>
      </Container>
    </DesktopOnly>
  )
}

export default DesktopMenu
